import './line-chart.scss';
import {Typography, useTheme} from '@mui/material';
import {
  VictoryChart,
  VictoryLine,
  VictoryVoronoiContainer,
  VictoryTooltip,
  VictoryAxis,
  ForAxes,
  DomainTuple,
  VictoryScatter,
} from 'victory';
import {ChartCard} from '../chart-card/chart-card';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../i18n/translations';
import {TimeLapseValue} from '../../../interfaces/time-lapse-value';
import {ErrorBox} from '../../error-box/error-box';

export interface LineChartDatum {
  x: string;
  y: number;
}

export type LineChartData = Array<LineChartDatum>;

/**
 * Line chart used in the statistics dashboards:
 * - x: string
 * - y: number (with unit or not)
 */
export const LineChart = (props: {
  title: string;
  xLabel: string;
  yLabel: string;
  data: LineChartData;
  loading: boolean;
  handleRefresh: () => void;
  timeLapse?: {
    values: TimeLapseValue;
    handleChange: (values: TimeLapseValue) => void;
  };
  subtitle?: string;
  yUnitLabel?: string;
  yDomain?: ForAxes<DomainTuple>;
  error?: string;
}) => {
  // Get theme
  const theme = useTheme();

  // Translations
  const {t} = useTranslation();

  return (
    <ChartCard
      title={props.title}
      subtitle={props.subtitle}
      timeLapse={props.timeLapse}
      handleRefresh={props.handleRefresh}
      loading={props.loading}
    >
      {props.data.length > 0 && !props.error && (
        <VictoryChart
          height={140}
          padding={{
            top: 16,
            bottom: 50,
            left: 50,
            right: 50,
          }}
          containerComponent={
            <VictoryVoronoiContainer
              labels={({datum}) => `${props.xLabel}:
           ${datum.x}\n \n${props.yLabel}: ${datum.y?.toFixed(props.yUnitLabel ? 2 : 0)} ${props.yUnitLabel || ''}`}
              labelComponent={
                <VictoryTooltip
                  constrainToVisibleArea
                  pointerLength={0}
                  flyoutStyle={{
                    stroke: theme.palette.divider,
                    fill: theme.palette.background.default,
                  }}
                />
              }
            />
          }
        >
          {/* Axis x */}
          <VictoryAxis
            label={props.xLabel}
            style={{
              axis: {
                stroke: theme.palette.divider,
              },
              tickLabels: {
                fontSize: 6,
                lineHeight: 1.57,
                fontFamily: 'Roboto,sans-serif',
                fill: theme.palette.text.primary,
                angle: props.data.length > 6 ? -90 : 0,
              },
              axisLabel: {
                padding: 32,
                fontWeight: 'bold',
                fontSize: 6,
                lineHeight: 1.57,
                fontFamily: 'Roboto,sans-serif',
                fill: theme.palette.text.primary,
              },
              grid: {
                stroke: theme.palette.divider,
                strokeWidth: 1,
              },
            }}
          />
          {/* Axis y */}
          <VictoryAxis
            dependentAxis
            fixLabelOverlap={true}
            label={props.yUnitLabel ? `${props.yLabel} (${props.yUnitLabel})` : props.yLabel}
            domain={props.yDomain}
            style={{
              axis: {
                stroke: theme.palette.divider,
              },
              tickLabels: {
                fontSize: 6,
                lineHeight: 1.57,
                fontFamily: 'Roboto,sans-serif',
                fill: theme.palette.text.primary,
              },
              axisLabel: {
                padding: 32,
                fontWeight: 'bold',
                fontSize: 6,
                lineHeight: 1.57,
                fontFamily: 'Roboto,sans-serif',
                fill: theme.palette.text.primary,
              },
              grid: {
                stroke: theme.palette.divider,
                strokeWidth: 1,
              },
            }}
          />
          {props.data.length > 1 ? (
            <VictoryLine
              data={props.data}
              style={{
                data: {
                  stroke: theme.palette.primary.main,
                  strokeOpacity: 1,
                  strokeWidth: 1,
                },
                labels: {
                  fontSize: 6,
                  fontFamily: 'Roboto,sans-serif',
                  fill: theme.palette.text.primary,
                },
              }}
            />
          ) : (
            <VictoryScatter
              style={{
                data: {fill: theme.palette.primary.main},
                labels: {
                  fontSize: 6,
                  fontFamily: 'Roboto,sans-serif',
                  fill: theme.palette.text.primary,
                },
              }}
              size={2}
              data={props.data}
            />
          )}
        </VictoryChart>
      )}
      {props.data.length === 0 && !props.error && (
        <Typography
          variant="subtitle2"
          component="span"
          color="text.secondary"
          className="line-chart-no-data-message"
        >
          {t(translations.charts.no_data)}
        </Typography>
      )}
      {props.error && <ErrorBox error={props.error} />}
    </ChartCard>
  );
};
